import React, { useState } from 'react'
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Space } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { parseDate } from '../../../config/dayjsConfig'
import { Voucher, VoucherQueryParams } from './types'
import { VoucherSearch } from './VoucherSearch'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { VoucherView } from '../../../features/vouchers/components/VoucherTable'
import { getRequestParams } from '../../../features/vouchers/utils/voucherUtils'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper/PageHeaderWrapper'
import VoucherForm from './components/VoucherForm'
import { TaskApplication } from '../../superuser/types'
import Authorized from '../../../components/Authorized/Authorized'
import { useCtxCurrentFiscalYear } from '../../../features/fiscalYear/queries/report/useCtxCurrentFiscalYear'
import { useCtxCreateVoucher } from '../../../features/vouchers/queries/useCtxCreateVoucher'
import { useCtxUpdateVoucher } from '../../../features/vouchers/queries/useCtxUpdateVoucher'
import { useCtxDeleteVoucher } from '../../../features/vouchers/queries/useCtxDeleteVoucher'
import { useXlsxDownload } from '../../../features/vouchers/queries/useXlsxDownload'

const Vouchers: React.FC = () => {
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)!
  const { data: currentFiscalYear } = useCtxCurrentFiscalYear()

  const [voucher, setVoucher] = useState<Voucher | null>(null)
  const [copyVoucher, setCopyVoucher] = useState<boolean>(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchParams, setSearchParams] = useState<Partial<VoucherQueryParams>>({
    from: parseDate(currentFiscalYear?.startDate) || undefined,
    until: parseDate(currentFiscalYear?.endDate) || undefined
  })
  const [open, setOpen] = useState(false)

  const { mutateAsync: createVoucher, isPending: createPending } = useCtxCreateVoucher()
  const { mutateAsync: updateVoucher, isPending: updatePending } = useCtxUpdateVoucher()
  const { mutateAsync: deleteVoucher } = useCtxDeleteVoucher()
  const { download: downloadVoucherXlsx, isDownloading: fileLoading } = useXlsxDownload(
    getRequestParams(searchParams),
    companyId
  )

  const handleEdit = (value: Voucher) => {
    setVoucher(value)
    setOpen(true)
  }

  const handleCopy = (value: Voucher) => {
    setCopyVoucher(true)
    setVoucher(value)
    setOpen(true)
  }

  const handleClose = () => {
    setVoucher(null)
    setOpen(false)
    setCopyVoucher(false)
  }

  const handleDelete = async ({ id }: Voucher) => {
    await deleteVoucher(id)
    setRefreshKey(prev => prev + 1)
  }

  const onCreate = async (values: FormData) => {
    await createVoucher(values)
    handleClose()
    setRefreshKey(prev => prev + 1)
  }

  const onUpdate = async (values: FormData) => {
    await updateVoucher({ data: values, voucherId: values.get('id') as Voucher['id'] })
    handleClose()
    setRefreshKey(prev => prev + 1)
  }

  return (
    <PageHeaderWrapper>
      <VoucherForm
        loading={createPending || updatePending}
        voucher={voucher}
        open={open}
        copyVoucher={copyVoucher}
        onCreate={onCreate}
        onUpdate={onUpdate}
        handleClose={handleClose}
      />
      <Flex gap={32} vertical>
        <VoucherSearch params={searchParams} setParams={setSearchParams} />
        <Card>
          <Flex justify="end" style={{ paddingBlock: 16 }}>
            <Space>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                loading={fileLoading}
                onClick={() => downloadVoucherXlsx()}
              >
                Excel
              </Button>
              <Authorized authority={{ accountingSoftware: TaskApplication.FINADECK }}>
                <Button icon={<PlusOutlined />} onClick={() => setOpen(true)}>
                  {t('vouchersPage:createVoucher')}
                </Button>
              </Authorized>
            </Space>
          </Flex>
          <VoucherView
            showActions
            showFilters={false}
            initialParams={searchParams}
            refreshKey={refreshKey}
            editVoucher={handleEdit}
            deleteVoucher={handleDelete}
            copyVoucher={handleCopy}
          />
        </Card>
      </Flex>
    </PageHeaderWrapper>
  )
}

export default Vouchers
